/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import { ActionIcon, Popover } from "rizzui";
import NotificationList from "./NotificationList";
import NotificationBadge from "./NotificationBadge";
import cn from "@/utils/class-names";
import { PiBell } from "react-icons/pi";
import NotificationRepository from "@/utilities/repositories/Notification";
import { NotificationItem, useNotification } from "./NotificationContext";
import { IAdminNotification } from "@/utilities/types/Notification";
import { useNavigate } from "react-router-dom";
import { routes } from "@/config/routes";
import notiPaginationDerivedAtom from "@/store/atoms/notification_atom";
import { useAtom } from "jotai";
import { NOTIFICATION_REFRESH } from "@/config/constants";

export interface INotificationProps {
  children?: React.ReactNode;
  className?: string;
  badgeClassName?: string;
  badgeSize?: "sm" | "md" | "lg";
  showBadge?: boolean;
  extraComponent?: React.ReactNode;
}

const NotificationTrigger: React.FC<INotificationProps> = ({
  children,
  className = "",
  badgeClassName = "",
  badgeSize = "sm",
  showBadge = true,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Popover
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      shadow="sm"
      placement={"bottom-end"}
    >
      <Popover.Trigger>
        <div className={cn("relative", className)}>
          {children}
          {showBadge && (
            <NotificationBadge
              className={cn("absolute top-0 right-0", badgeClassName)}
              size={badgeSize}
            />
          )}
        </div>
      </Popover.Trigger>
      <Popover.Content className="z-[9999] p-0 dark:bg-gray-900">
        <NotificationList onClose={() => setIsOpen(false)} />
      </Popover.Content>
    </Popover>
  );
};

export default function Notification({
  children,
  ...props
}: INotificationProps) {
  const { pushDefaultNotifications, updateUnreadCountApi } = useNotification();
  const [, setPagination] = useAtom(notiPaginationDerivedAtom);

  const fetchNotifications = async () => {
    const res = await NotificationRepository.getAdminNotifications({
      queryParams: {
        pagination: {
          page: 1,
          limit: 10,
        },
      },
    });

    if (!res?.data) return;

    const notifications: NotificationItem[] =
      res.data?.items?.map((item: IAdminNotification) => ({
        id: item.id,
        title: item.title,
        message: item.description,
        timestamp: item.createdAt,
        isRead: item.isRead,
        type: item.type,
        userId: item.data?.userId,
        data: item.data,
      })) || [];

    pushDefaultNotifications(notifications);
    updateUnreadCountApi(res.data?.unreadCount);
    setPagination({
      total: res.data?.total || 0,
      limit: res.data?.limit || 10,
      currentPage: res.data?.currentPage || 1,
      totalPage: res.data?.totalPage || 0,
    });
  };

  React.useEffect(() => {
    fetchNotifications();
    const intervalId = setInterval(
      () => fetchNotifications(),
      NOTIFICATION_REFRESH
    );
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <NotificationTrigger {...props}>
      {children || (
        <ActionIcon size="lg" variant="outline" className="rounded-full">
          <PiBell className="h-5 w-5 animate-popup" />
        </ActionIcon>
      )}
    </NotificationTrigger>
  );
}
